// URS-F416d: componente reutilizable para gestionar los bloqueos de
// agenda de un usuario. Se usa desde:
//   - Configuracion > Usuarios > modal Editar Usuario (admin gestiona los
//     bloqueos de cualquier usuario).
//   - /profile (Mi Perfil) — cada usuario gestiona los suyos SIN necesitar
//     permisos de Configuracion.
//
// El backend `/api/user-schedule-blocks` ya restringe: un usuario no admin
// solo puede crear/borrar/listar los suyos propios (403 si intenta con
// otro user_id). Por lo tanto seguro exponerlo aqui.
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Trash } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserScheduleBlocksSection({ userId, lang, hideCard = false, initialDate = null }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const defaultDate = initialDate || today;
  const [form, setForm] = useState({ date: defaultDate, all_day: false, start_time: '09:00', end_time: '13:00', reason: '' });

  // URS-F416f: si cambia el initialDate (p.ej. usuario navega en la agenda
  // y abre el modal), sincronizamos el campo Fecha del formulario.
  useEffect(() => {
    if (initialDate) setForm(p => ({ ...p, date: initialDate }));
  }, [initialDate]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/user-schedule-blocks?user_id=${userId}`,
        { withCredentials: true },
      );
      setBlocks(data?.blocks || []);
    } catch {
      // noop
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.reason.trim()) {
      toast.error(lang === 'es' ? 'Motivo obligatorio' : 'Reason required');
      return;
    }
    if (!form.all_day && form.start_time >= form.end_time) {
      toast.error(lang === 'es' ? 'Hora fin debe ser mayor que inicio' : 'End time must be after start');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/user-schedule-blocks`, {
        user_id: userId,
        date: form.date,
        all_day: form.all_day,
        start_time: form.all_day ? null : form.start_time,
        end_time: form.all_day ? null : form.end_time,
        reason: form.reason.trim(),
      }, { withCredentials: true });
      setForm({ date: defaultDate, all_day: false, start_time: '09:00', end_time: '13:00', reason: '' });
      toast.success(lang === 'es' ? 'Bloqueo creado' : 'Block created');
      load();
    } catch (err) {
      toast.error(errText(err));
    }
    setCreating(false);
  };

  const removeBlock = async (b) => {
    if (!window.confirm(lang === 'es' ? `Eliminar bloqueo del ${b.date}?` : `Delete block of ${b.date}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/user-schedule-blocks/${b.block_id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Bloqueo eliminado' : 'Block removed');
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const inner = (
    <>
      {!hideCard && (
        <div className="border-b border-stone-100 px-4 py-2 bg-stone-50">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-stone-800">
              {lang === 'es' ? 'Bloqueos de agenda' : 'Schedule blocks'}
            </span>
            <span className="text-[11px] text-stone-500">
              {lang === 'es'
                ? '(no cierra huecos si otros IP/CoIP del estudio quedan disponibles)'
                : '(does not close slots when other study IP/CoIP remain available)'}
            </span>
          </div>
        </div>
      )}
      <div className="p-4 space-y-3">
        {/* URS-F426: formulario "Añadir bloqueo" sticky para que sea visible
            aunque el usuario tenga muchos bloqueos y haga scroll en la lista. */}
        <div className="grid grid-cols-12 gap-2 items-end sticky top-0 bg-white z-10 pb-3 border-b border-stone-200">
          <div className="col-span-3">
            <Label className="text-[11px] text-stone-600">{lang === 'es' ? 'Fecha' : 'Date'}</Label>
            <input
              type="date"
              value={form.date}
              onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
              className="w-full h-8 border border-stone-300 rounded-sm px-2 text-sm"
              data-testid="user-block-date"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] text-stone-600">{lang === 'es' ? 'Todo el día' : 'All day'}</Label>
            <div className="h-8 flex items-center">
              <Switch
                checked={form.all_day}
                onCheckedChange={v => setForm(p => ({ ...p, all_day: v }))}
                data-testid="user-block-all-day"
              />
            </div>
          </div>
          {!form.all_day && (
            <>
              <div className="col-span-2">
                <Label className="text-[11px] text-stone-600">{lang === 'es' ? 'Inicio' : 'Start'}</Label>
                <input
                  type="time"
                  value={form.start_time}
                  onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))}
                  className="w-full h-8 border border-stone-300 rounded-sm px-2 text-sm"
                  data-testid="user-block-start"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] text-stone-600">{lang === 'es' ? 'Fin' : 'End'}</Label>
                <input
                  type="time"
                  value={form.end_time}
                  onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))}
                  className="w-full h-8 border border-stone-300 rounded-sm px-2 text-sm"
                  data-testid="user-block-end"
                />
              </div>
            </>
          )}
          <div className={form.all_day ? 'col-span-7' : 'col-span-3'}>
            <Label className="text-[11px] text-stone-600">{lang === 'es' ? 'Motivo' : 'Reason'}</Label>
            <input
              type="text"
              value={form.reason}
              onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
              placeholder={lang === 'es' ? 'Vacaciones, formación, reunión...' : 'Vacation, training, meeting...'}
              className="w-full h-8 border border-stone-300 rounded-sm px-2 text-sm"
              data-testid="user-block-reason"
            />
          </div>
          <div className="col-span-12 flex justify-end">
            <Button size="sm" onClick={submit} disabled={creating} data-testid="user-block-create">
              {creating ? (lang === 'es' ? 'Creando...' : 'Creating...') : (lang === 'es' ? '+ Añadir bloqueo' : '+ Add block')}
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-xs text-stone-400">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
        ) : blocks.length === 0 ? (
          <p className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-sm px-3 py-2" data-testid="user-blocks-empty">
            {lang === 'es' ? 'Sin bloqueos programados.' : 'No blocks scheduled.'}
          </p>
        ) : (
          <div className="border border-stone-200 rounded-sm overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="bg-stone-100 text-stone-600 text-[11px]">
                <tr>
                  <th className="text-left px-3 py-1.5">{lang === 'es' ? 'Fecha' : 'Date'}</th>
                  <th className="text-left px-3 py-1.5">{lang === 'es' ? 'Horas' : 'Time'}</th>
                  <th className="text-left px-3 py-1.5">{lang === 'es' ? 'Motivo' : 'Reason'}</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {blocks.map(b => (
                  <tr key={b.block_id} className="border-t border-stone-100" data-testid={`user-block-row-${b.block_id}`}>
                    <td className="px-3 py-1.5 text-stone-800 font-mono">{b.date}</td>
                    <td className="px-3 py-1.5 text-stone-700">
                      {b.all_day
                        ? (lang === 'es' ? 'Todo el día' : 'All day')
                        : `${b.start_time} — ${b.end_time}`}
                    </td>
                    <td className="px-3 py-1.5 text-stone-800">{b.reason}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBlock(b)}
                        className="h-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`user-block-delete-${b.block_id}`}
                      >
                        <Trash size={12} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  if (hideCard) return <div data-testid="user-blocks-section">{inner}</div>;
  return (
    <div className="border border-stone-200 rounded-sm bg-white" data-testid="user-blocks-section">
      {inner}
    </div>
  );
}
